// app/javascript/controllers/kanban_controller.js
import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["grid", "headerCell", "bodyCell", "scrollContainer", "dropZone", "cardsContainer"]

  static values = {
    componentId: String,
    columnCount: Number,
    placingUrl: String,
    moveCardUrl: String
  }

  connect() {
    // СНАЧАЛА — поля/состояние
    this.sortableInstances = []
    this.currentDraggedCard = null
    this.originalContainer = null
    this.originalIndex = null

    // ПОТОМ — настройка/инициализация
    this.setupGrid()
    this.observeResize()
    this.initSortables()
    this.initDropZone()
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    // Уничтожаем все Sortable инстансы
    this.sortableInstances.forEach(sortable => {
      if (sortable && sortable.destroy) {
        sortable.destroy()
      }
    })

    this.sortableInstances = []
  }

  // ========== GRID SETUP ==========

  setupGrid() {
    const columnCount = this.columnCountValue || 1
    const gridTemplate = `minmax(130px, 200px) repeat(${columnCount}, minmax(180px, 1fr))`

    if (this.hasGridTarget) {
      this.gridTarget.style.gridTemplateColumns = gridTemplate
    }
  }

  observeResize() {
    this.resizeObserver = new ResizeObserver(() => {
      this.adjustColumnWidths()
    })
    this.resizeObserver.observe(this.element)
  }

  adjustColumnWidths() {
    const container = this.hasScrollContainerTarget ? this.scrollContainerTarget : null
    if (!container) return

    const containerWidth = container.clientWidth
    const columnCount = this.columnCountValue || 1
    const firstColumnWidth = 130
    const minColumnWidth = 180

    const availableWidth = containerWidth - firstColumnWidth
    const calculatedWidth = Math.max(minColumnWidth, availableWidth / columnCount)

    if (this.hasGridTarget) {
      const gridTemplate = `${firstColumnWidth}px repeat(${columnCount}, minmax(${calculatedWidth}px, 1fr))`
      this.gridTarget.style.gridTemplateColumns = gridTemplate
    }
  }

  // ========== SORTABLE INITIALIZATION ==========

  initSortables() {
    console.log('Initializing sortables for', this.cardsContainerTargets.length, 'containers')
    this.cardsContainerTargets.forEach((container, index) => {
      console.log('Initializing sortable for container', index, container)
      const sortable = new Sortable(container, {
        group: "kanban-cards",
        animation: 200,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
        ghostClass: "sortable-ghost",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        handle: ".kanban-handle",

        // Фильтруем клики по ссылкам и кнопкам
        filter: "a, button, [data-action]",
        preventOnFilter: false,

        // Задержка для предотвращения случайного перетаскивания
        delay: 50,
        delayOnTouchOnly: true,

        // Разрешаем сортировку внутри группы
        sort: true,

        onStart: (evt) => this.onSortableStart(evt),
        onEnd: (evt) => this.onSortableEnd(evt),
        onAdd: (evt) => this.onCardMoved(evt),
        onUpdate: (evt) => this.onCardReordered(evt)
      })

      this.sortableInstances.push(sortable)
    })
    console.log('Finished initializing', this.sortableInstances.length, 'sortables')
  }

  onSortableStart(evt) {
    this.currentDraggedCard = evt.item
    this.originalContainer = evt.from

    // Сохраняем оригинальную позицию для возможного отката
    this.originalIndex = evt.oldIndex

    this.showDropZone()

    // Добавляем класс к body для глобальных стилей
    document.body.classList.add('kanban-dragging')

    // Подсвечиваем все возможные drop-зоны
    this.cardsContainerTargets.forEach(container => {
      if (container !== evt.from) {
        this.addHighlightToContainer(container)
      }
    })

    console.log('Drag started:', {
      recordId: evt.item.dataset.recordId,
      documentUid: evt.item.dataset.documentUid
    })
  }

  onSortableEnd(evt) {
    this.currentDraggedCard = null
    this.originalContainer = null
    this.originalIndex = null

    this.hideDropZone()

    document.body.classList.remove('kanban-dragging')

    // Убираем подсветку со всех контейнеров
    this.cardsContainerTargets.forEach(container => {
      this.removeHighlightFromContainer(container)
      container.classList.remove('drag-over')
    })

    console.log('Drag ended')
  }

  onCardMoved(evt) {
    const card = evt.item
    const fromContainer = evt.from
    const toContainer = evt.to

    // Проверяем что это не drop zone
    if (toContainer === this.dropZoneTarget) {
      return // Обрабатывается в onDropToPlacing
    }

    const recordId = card.dataset.recordId
    const documentUid = card.dataset.documentUid
    const newX = toContainer.dataset.kanbanX
    const newY = toContainer.dataset.kanbanY
    const oldX = fromContainer.dataset.kanbanX
    const oldY = fromContainer.dataset.kanbanY

    console.log(`Card ${recordId} moved from (${oldX}, ${oldY}) to (${newX}, ${newY})`)

    // Убираем placeholder если он есть в целевой ячейке
    this.removePlaceholder(toContainer)

    // Добавляем placeholder в исходную ячейку если она пуста
    this.addPlaceholderIfEmpty(fromContainer)

    // Отправляем запрос на сервер
    this.sendMoveCardRequest({
      recordId,
      documentUid,
      newX,
      newY,
      oldX,
      oldY,
      componentId: this.componentIdValue
    })

    // Dispatch event для внешних обработчиков
    this.dispatch('cardMoved', {
      detail: { recordId, documentUid, newX, newY, oldX, oldY }
    })
  }

  onCardReordered(evt) {
    // Карточка перемещена внутри той же ячейки
    const card = evt.item
    const container = evt.to
    const recordId = card.dataset.recordId
    const newIndex = evt.newIndex
    const oldIndex = evt.oldIndex

    console.log(`Card ${recordId} reordered from ${oldIndex} to ${newIndex}`)

    // Можно отправить запрос если важен порядок
    // this.sendReorderRequest({ recordId, newIndex, ... })
  }

  removePlaceholder(container) {
    const cell = container.closest('[data-kanban-cell]')
    const placeholder = cell?.querySelector('[data-kanban-empty-placeholder]')
    if (placeholder) {
      placeholder.remove()
    }
  }

  addPlaceholderIfEmpty(container) {
    if (container.children.length === 0) {
      const cell = container.closest('[data-kanban-cell]')
      if (cell && !cell.querySelector('[data-kanban-empty-placeholder]')) {
        const placeholder = document.createElement('div')
        placeholder.className = 'text-gray-300 text-xs text-center py-4'
        placeholder.dataset.kanbanEmptyPlaceholder = ''
        placeholder.textContent = '—'
        cell.appendChild(placeholder)
      }
    }
  }

  async sendMoveCardRequest(data) {
    const url = this.moveCardUrlValue

    if (!url || url === '' || url === '#') {
      console.warn('Move card URL not configured, skipping server request')
      return
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      if (result.success) {
        this.showNotification('Карточка перемещена', 'success')
      } else {
        this.showNotification(result.message || 'Ошибка перемещения', 'error')
        // Откатываем изменения
        this.rollbackMove()
      }
    } catch (error) {
      console.error('Error moving card:', error)
      this.showNotification('Ошибка сети при перемещении', 'error')
      // Откатываем изменения
      this.rollbackMove()
    }
  }

  rollbackMove() {
    // Если есть информация для отката - перезагружаем страницу
    // В идеале нужно вернуть карточку на место
    console.warn('Move failed, consider refreshing the page')

    // Опционально: перезагрузка через Turbo
    // Turbo.visit(window.location.href, { action: 'replace' })
  }

  // ========== DROP ZONE ==========

  initDropZone() {
    if (!this.hasDropZoneTarget) {
      console.warn('Drop zone target not found')
      return
    }

    const dropZone = this.dropZoneTarget

    // Делаем drop zone тоже Sortable target
    const dropZoneSortable = new Sortable(dropZone, {
      group: {
        name: "kanban-cards",
        pull: false,  // Нельзя вытащить из drop zone
        put: true     // Можно положить в drop zone
      },
      animation: 200,
      ghostClass: "sortable-ghost",
      sort: false,    // Нет сортировки внутри drop zone

      onAdd: (evt) => this.onDropToPlacing(evt)
    })

    this.sortableInstances.push(dropZoneSortable)

    // Дополнительные обработчики для визуальных эффектов
    dropZone.addEventListener('dragenter', (e) => {
      e.preventDefault()
      dropZone.classList.add('drag-over')
    })

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropZone.classList.add('drag-over')
    })

    dropZone.addEventListener('dragleave', (e) => {
      // Проверяем что мы действительно покинули зону
      if (!dropZone.contains(e.relatedTarget)) {
        dropZone.classList.remove('drag-over')
      }
    })

    dropZone.addEventListener('drop', () => {
      dropZone.classList.remove('drag-over')
    })
  }

  showDropZone() {
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add('active')
      this.dropZoneTarget.classList.remove('hidden')

      // Анимация появления
      requestAnimationFrame(() => {
        this.dropZoneTarget.style.opacity = '1'
        this.dropZoneTarget.style.transform = 'translateY(0)'
      })
    }
  }

  hideDropZone() {
    if (!this.hasDropZoneTarget) return

    this.dropZoneTarget.classList.remove('drag-over')
    this.dropZoneTarget.style.opacity = '0'
    this.dropZoneTarget.style.transform = 'translateY(10px)'

    setTimeout(() => {
      if (!this.currentDraggedCard) {
        this.dropZoneTarget.classList.remove('active')
        this.dropZoneTarget.classList.add('hidden')
      }
    }, 300)
  }

  addHighlightToContainer(container) {
    container.classList.add('sortable-drag-over')
  }

  removeHighlightFromContainer(container) {
    container.classList.remove('sortable-drag-over')
  }

  async onDropToPlacing(evt) {
    const card = evt.item
    const documentUid = card.dataset.documentUid
    const recordId = card.dataset.recordId
    const fromContainer = evt.from

    console.log('Card dropped to placing zone:', { documentUid, recordId })

    // Показываем индикатор загрузки на карточке
    card.classList.add('placing-in-progress')
    this.showCardLoader(card)

    // Отправляем запрос на размещение
    const success = await this.sendPlacingRequest(documentUid, recordId)

    if (success) {
      // Успех - удаляем карточку с анимацией
      this.removeCardWithAnimation(card)

      // Добавляем placeholder в исходную ячейку если она пуста
      this.addPlaceholderIfEmpty(fromContainer)

      // Dispatch event
      this.dispatch('cardPlaced', {
        detail: { documentUid, recordId }
      })
    } else {
      // Ошибка - возвращаем карточку обратно
      this.returnCardToOrigin(card, fromContainer, evt.oldIndex)
    }

    // Убираем индикатор загрузки
    card.classList.remove('placing-in-progress')
    this.hideCardLoader(card)

    // Очищаем drop zone от карточки (если она ещё там)
    if (this.hasDropZoneTarget && this.dropZoneTarget.contains(card)) {
      this.dropZoneTarget.removeChild(card)
    }
  }

  async sendPlacingRequest(documentUid, recordId) {
    const url = this.placingUrlValue
    console.log('Sending placing request to:', url)
    if (!url || url === '' || url === '#') {
      console.error('Placing URL not configured!')
      this.showNotification('URL для размещения не настроен', 'error')
      return false
    }

    if (!documentUid) {
      console.error('Document UID is missing!')
      this.showNotification('Отсутствует UID документа', 'error')
      return false
    }

    try {
      this.showNotification('Выполняется размещение...', 'info')

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          document_uid: documentUid,
          record_id: recordId,
          component_id: this.componentIdValue
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()

      if (result.success) {
        this.showNotification(result.message || 'Размещение выполнено успешно!', 'success')
        return true
      } else {
        this.showNotification(result.message || 'Ошибка размещения', 'error')
        return false
      }
    } catch (error) {
      console.error('Error placing document:', error)
      this.showNotification(`Ошибка: ${error.message}`, 'error')
      return false
    }
  }

  returnCardToOrigin(card, originalContainer, originalIndex) {
    if (!originalContainer) {
      console.warn('Original container not found, cannot return card')
      return
    }

    // Удаляем из drop zone
    if (card.parentNode) {
      card.parentNode.removeChild(card)
    }

    // Вставляем обратно в оригинальный контейнер
    const children = originalContainer.children
    if (originalIndex >= children.length) {
      originalContainer.appendChild(card)
    } else {
      originalContainer.insertBefore(card, children[originalIndex])
    }

    // Анимация возврата
    card.style.animation = 'returnToOrigin 0.3s ease-out'
    setTimeout(() => {
      card.style.animation = ''
    }, 300)
  }

  removeCardWithAnimation(card) {
    card.style.transition = 'all 0.3s ease-out'
    card.style.transform = 'scale(0.8)'
    card.style.opacity = '0'

    setTimeout(() => {
      if (card.parentNode) {
        card.parentNode.removeChild(card)
      }
    }, 300)
  }

  showCardLoader(card) {
    // Добавляем оверлей с лоадером на карточку
    const loader = document.createElement('div')
    loader.className = 'card-loader-overlay'
    loader.innerHTML = `
      <div class="card-loader-spinner">
        <svg class="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `
    card.style.position = 'relative'
    card.appendChild(loader)
  }

  hideCardLoader(card) {
    const loader = card.querySelector('.card-loader-overlay')
    if (loader) {
      loader.remove()
    }
  }

  // ========== UTILITY METHODS ==========

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  showNotification(message, type = 'info') {
    // Используем существующую систему уведомлений или создаём простую
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500'
    }

    const icons = {
      success: '✓',
      error: '✗',
      warning: '⚠',
      info: 'ℹ'
    }

    // Удаляем предыдущее уведомление
    const existingNotification = document.querySelector('.kanban-notification')
    if (existingNotification) {
      existingNotification.remove()
    }

    const notification = document.createElement('div')
    notification.className = `
      kanban-notification
      fixed bottom-4 right-4 z-50
      flex items-center gap-3
      px-4 py-3 rounded-lg shadow-lg
      text-white text-sm font-medium
      transform transition-all duration-300
      ${colors[type] || colors.info}
    `
    notification.innerHTML = `
      <span class="text-lg">${icons[type] || icons.info}</span>
      <span>${message}</span>
    `

    document.body.appendChild(notification)

    // Анимация появления
    requestAnimationFrame(() => {
      notification.style.opacity = '1'
      notification.style.transform = 'translateY(0)'
    })

    // Автоскрытие
    const duration = type === 'error' ? 5000 : 3000
    setTimeout(() => {
      notification.style.opacity = '0'
      notification.style.transform = 'translateY(10px)'
      setTimeout(() => notification.remove(), 300)
    }, duration)
  }

  // ========== EVENT HANDLERS ДЛЯ ОБРАТНОЙ СОВМЕСТИМОСТИ ==========
  // (на случай если где-то используются старые data-action)

  handleDragStart(event) {
    // Для обратной совместимости с нативным drag
    console.warn('Native drag used, consider using Sortable handle')
  }

  handleDragEnd(event) {
    // Для обратной совместимости
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  dragLeave(event) {
    // Для обратной совместимости
  }

  drop(event) {
    event.preventDefault()
    console.warn('Native drop used, this should be handled by Sortable')
  }
}
